import { createReducer, on } from '@ngrx/store';
import { AchInfo } from '../../create-request/models/ach/AchInfo';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { StwProcessCode, amountTypeDict } from '../../shared/utilities/Constants';
import { StandingAuthAchActions, StandingAuthConfirmationPageActions, StandingAuthDetailsActions, StandingAuthFormActions, StandingAuthReviewPageActions } from '../actions';
import { SharedActions } from '../../shared/actions';
import { AmountType } from '../../shared/types/Transaction';
import { StandingAuthorizationsActions } from '../../create-request/actions';

export const standingAuthAchInfoKey = 'achInfo';

export type State = AchInfo;

export const initialStandingAuthAchInfoState: State = {
  defaultProcessDate: '',
  nickName: '',
  withholdFedTax: false,
  withholdStateTax: false,
  fedTaxPercent: '',
  fedTaxAmount: '',
  stateTaxPercent: '',
  stateTaxAmount: '',
  canDisplayStateTax: false,
  distributionReason: '',
  distributionReasonsList: [],
  stateCd: '',
  stateTypeIndicator: '',
  stateTaxAttentionRequiredMI: false,
  stateTaxAttentionRequiredOther: false,
  stwProcessCode: '',
  maxTransferAmount: '',
  toFromAccountNumber: '',
  amountType: amountTypeDict.gross,
  transferAmount: '',
  withdrawalAmount: '',
  profileAmount: '',
  contributionOptions: [],
  contributionReason: '',
  contributionCode: '',
  contributionSubCode: '',
  standingAuthorizationsCallSucceeded: false,
  grossNetUpdated: false,
  distributionReasonUpdated: false,
  inputCallFinished: false,
  verifyCallFinished: false,
  inquiryCallFinished: false,
  acknowledgeCallFinished: false,
  availableCashMMF: '',
  availableCashMargin: '',
  pendReasons: [],
  acknowledgementArtifactContent: '',
  toFromAccount: {
    abaNumber: '',
    accountName: '',
    accountNumber: '',
    accountType: '',
    financialInstitution: ''
  },
  endDate: '',
  messageFields: {
    errorList: [],
    warningList: [],
    hasError: false,
    hasWarning: false
  },
  attentionMessageList: [],
  moneyLinkType: '',
  frequencyList: [],
  distributionReasonMap: [],
  frequency: '',
  fiAccountType: '',
  cashieringDates: { initiationDate: '' },
  cashieringIraAgreementText: '',
  isCreateAchProfile: false,
  processDate: '',
  cashieringDateCallFinished: false,
  secondProcessDate: '',
  defaultFedTaxPercent: '',
  defaultStateTaxPercent: '',
  defaultAmountType: '',
  defaultDistributionReason: '',
  cashieringRequestId: '',
  authResponse: {
    draftId: '',
    pendForRelease: false,
    pendForReleaseReason: '',
    canRelease: false
  },
  thirdPartyInd: '',
  revalidation: {
    status: '',
    errors: [],
    fieldsUpdated: []
  },
  refId: '',
  confirmationId: '',
  transferId: ''
};

export const standingAuthAchReducer = createReducer(
  initialStandingAuthAchInfoState,
  on(
    StandingAuthReviewPageActions.cancelClicked,
    StandingAuthConfirmationPageActions.returnStandingAuthClicked,
    SharedActions.cancelModalContinueClicked,
    () => {
      return { ...initialStandingAuthAchInfoState, };
    }
  ),
  on(StandingAuthAchActions.callAchInquiry, (state) => {
    return {
      ...initialStandingAuthAchInfoState,
      standingAuthorizationsCallSucceeded: state.standingAuthorizationsCallSucceeded,
      loading: true
    };
  }),
  on(StandingAuthAchActions.inquiryCallSuccessful, (state, { achInquiryResponse }) => {
    const percentFormatPipe = new FormatPercentPipe();
    const contributionList: string[] = [];
    const contributiondate = achInquiryResponse.contributionReason.substring(0, 4) + ' - ' + achInquiryResponse.contributionReason.substring(5);
    contributionList.push(contributiondate);
    return {
      ...state,
      amountType: achInquiryResponse.grossNetIndicator === 'G' ? amountTypeDict.gross : achInquiryResponse.grossNetIndicator === 'N' ? amountTypeDict.net : '',
      availableCashMargin: achInquiryResponse.availableOnMargin,
      availableCashMMF: achInquiryResponse.availableCashMmf,
      canDisplayStateTax: achInquiryResponse.canDisplayStateTax,
      cashieringIraAgreementText: achInquiryResponse.cashieringIraAgreementText,
      contributionReason: achInquiryResponse.contributionReason,
      contributionOptions: contributionList,
      defaultFedTaxPercent: achInquiryResponse.iraFields.defaultFedTax,
      defaultProcessDate: achInquiryResponse.moneyLinkBusinessDate,
      defaultStateTaxPercent: achInquiryResponse.iraFields.defaultStateTax,
      distributionReason: achInquiryResponse.iraFields.isIraTransaction ? achInquiryResponse.distributionReasonDescription : '',
      distributionReasonMap: achInquiryResponse.iraFields.distributionReasonMap,
      endDate: achInquiryResponse.endDate,
      fedTaxAmount: `$${achInquiryResponse.iraFields.fedTaxAmount}`,
      fedTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.fedTaxPercentage) : '',
      frequency: achInquiryResponse.frequency,
      frequencyList: achInquiryResponse.frequencyList,
      inquiryCallFinished: true,
      maxTransferAmount: achInquiryResponse.maxTransferAmount,
      moneyLinkType: achInquiryResponse.moneyLinkType,
      pendReasons: achInquiryResponse.pendReasonsList,
      processDate: achInquiryResponse.processDate,
      secondProcessDate: achInquiryResponse.secondProcessDate,
      stateCd: achInquiryResponse.stateCode,
      stateTaxAmount: `$${achInquiryResponse.iraFields.stateTaxAmount}`,
      stateTaxAttentionRequiredMI: achInquiryResponse.iraFields.stateTaxAttentionRequiredMI,
      stateTaxAttentionRequiredOther: achInquiryResponse.iraFields.stateTaxAttentionRequired,
      stateTaxPercent: achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(achInquiryResponse.iraFields.stateTaxPercentage, 3) : '',
      stateTypeIndicator: achInquiryResponse.iraFields.stateTypeIndicator,
      stwProcessCode: achInquiryResponse.iraFields.stwProcessCode as StwProcessCode,
      toFromAccount: achInquiryResponse.toFromAccount,
      toFromAccountNumber: achInquiryResponse.toFromAccount.accountNumber,
      transferAmount: achInquiryResponse.netAmt,
      withdrawalAmount: achInquiryResponse.grossAmt,
      withholdFedTax: achInquiryResponse.iraFields.isWithholdFedTax,
      withholdStateTax: achInquiryResponse.iraFields.isWithholdStateTax,
    };
  }),
  on(StandingAuthAchActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      error: err.message,
      hasError: true,
    };
  }),
  on(StandingAuthorizationsActions.loadSloas, (state) => {
    return {
      ...state,
      standingAuthorizationsCallSucceeded: false,
    };
  }),
  on(
    StandingAuthorizationsActions.loadSloasSuccess,
    (state) => {
      return {
        ...state,
        standingAuthorizationsCallSucceeded: true,
      };
    }
  ),
  on(
    StandingAuthorizationsActions.loadSloasFailure,
    (state) => {
      return {
        ...state,
        standingAuthorizationsCallSucceeded: false,
      };
    }
  ),
  on(
    StandingAuthAchActions.callAchVerify,
    StandingAuthAchActions.callAcknowledge,
    SharedActions.cancelModalContinueClicked,
    (state) => {
      return {
        ...state,
        messageFields: {
          errorList: [],
          warningList: [],
          hasError: false,
          hasWarning: false
        },
      };
    }
  ),
  on(StandingAuthAchActions.callAchVerify, (state) => {
    return {
      ...state,
      verifyCallFinished: false,
    };
  }),
  on(StandingAuthFormActions.distributionReasonChanged, (state, action) => {
    return {
      ...state,
      distributionReason: action.distributionReason.description
    };
  }),
  on(StandingAuthAchActions.verifyCallSuccessful, (state, { response }) => {
    return {
      ...state,
      fedTaxAmount: response.iraFields.fedTaxFormatted,
      stateTaxAmount: response.iraFields.stateTaxFormatted,
      fedTaxPercent: response.iraFields.fedTaxFormattedPercentage,
      stateTaxPercent: response.iraFields.stateTaxFormattedPercentage,
      amountType: mapAmountType(response.messageFields?.hasError, state.amountType, response.iraFields.grossNetIndicator),
      messageFields: {
        errorList: response.messageFields.errorList,
        warningList: response.messageFields.warningList,
        hasError: response.messageFields.errorList.length > 0,
        hasWarning: response.messageFields.warningList.length > 0
      },
      attentionMessageList: response.attentionMessagesList,
      transferAmount: response.transferAmount,
      withdrawalAmount: response.totalWithdrawalAmount,
      profileAmount: response.profileAmount,
      pendReasons: response.pendReasonsList,
      moneyLinkType: response.moneyLinkType,
      distributionReason: response.isIraTransaction ? response.distributionReasonDescription : '',
      verifyCallFinished: true,
      acknowledgementArtifactContent: response.acknowledgementArtifactContent,
      processDate: response.processDate,
      refId: response.refId ?? '',
      confirmationId: response.confirmationId ?? '',
      transferId: response.transferId ?? ''
    };
  }),
  on(
    StandingAuthAchActions.verifyCallFinishedWithError,
    (state, { response }) => {
      return {
        ...state,
        messageFields: {
          errorList: response.messageFields.errorList,
          warningList: response.messageFields.warningList,
          hasError: response.messageFields.errorList.length > 0,
          hasWarning: response.messageFields.warningList.length > 0
        },
        verifyCallFinished: false,
      };
    }
  ),
  on(StandingAuthReviewPageActions.editClicked, (state) => {
    return {
      ...state,
      verifyCallFinished: false,
    };
  }),
  on(
    StandingAuthAchActions.acknowledgeCallSuccessful,
    (state, { response }) => {
      return {
        ...state,
        acknowledgeCallFinished: true,
        messageFields: response.messageFields,
        loginid: response.lastInitiatedBy,
        source: response.source.description
      };
    }
  ),
  on(
    StandingAuthAchActions.acknowledgeCallFinishedWithError,
    (state, { response }) => {
      return {
        ...state,
        messageFields: {
          errorList: response.messageFields.errorList,
          warningList: [],
          hasError: true,
          hasWarning: false
        },
        acknowledgeCallFinished: false,
      };
    }
  ),
  on(
    StandingAuthAchActions.verifyCallSuccessful,
    StandingAuthAchActions.acknowledgeCallSuccessful,
    (state) => {
      return {
        ...state,
        serviceError: resetServiceError(),
      };
    }
  ),
  on(StandingAuthDetailsActions.loadWithdrawalDateSuccess, (state, cashieringDates) => {
    return {
      ...state,
      cashieringDates: { initiationDate: cashieringDates.initiationDate }
    };
  }),
  on(
    StandingAuthDetailsActions.loadWithdrawalDateFailure,
    StandingAuthDetailsActions.loadWithdrawalDate,
    (state) => {
      return {
        ...state,
        cashieringDates: initialStandingAuthAchInfoState.cashieringDates,
      };
    }
  )
);

function resetServiceError() {
  return {
    headers: {
      normalizedNames: {},
      lazyUpdate: false,
    },
    status: 0,
    statusText: '',
    url: '',
    ok: false,
    name: '',
    message: '',
    loading: false,
    error: {
      errorDescription: '',
      error: '',
    },
  };
}
function mapAmountType(hasError: boolean, currentValue: AmountType, newValue: string): AmountType {
  if (hasError) {
    return currentValue;
  }
  if (newValue === 'G') {
    return amountTypeDict.gross;
  }
  return newValue === 'N' ? amountTypeDict.net : '';
}
